import { InfoOutlined, QrCodeScanner, Share } from "@mui/icons-material";
import { Box } from "@mui/material";
import { usePayPointQuery } from "Api/Queries/PayPoints/usePayPointQuery";
import {
  type DrawerState,
  ContentDrawer,
} from "Components/Transactions/TransactionActionButtons/ContentDrawer";
import { IconActionButton } from "Components/Shared/Buttons/IconActionButton";
import type { TransactionDetailDto } from "Infrastructure/Api/Api";
import { TransactionStatus } from "Infrastructure/Api/Api";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { formatCurrency } from "Utils/CurrencyUtils";
import { appUrl } from "Utils/UrlUtils";

type Props = {
  transactionsDetail: TransactionDetailDto;
  isInfoHidden?: boolean;
};

export const TransactionActionButtons = ({
  transactionsDetail,
  isInfoHidden,
}: Props) => {
  const [drawerState, setDrawerState] = useState<DrawerState>("closed");
  const { t } = useResource();

  const { data: paypoint } = usePayPointQuery(
    transactionsDetail.payPointPublicID,
  );

  const amount = formatCurrency({
    amount: transactionsDetail.amount,
    currencyCode: transactionsDetail.currencyCode,
  });

  const handleShareClick = async () => {
    const isTransactionSuccessful =
      transactionsDetail.transactionStatus === TransactionStatus.Success;

    const shareContent = isTransactionSuccessful
      ? {
          title: t(Resources.Merchant.InitiateDetail.ShareReceiptTitle),
          text: t(Resources.Merchant.InitiateDetail.ShareReceiptText, {
            transactionId: transactionsDetail.publicID,
            amount,
            note: transactionsDetail.noteForDebtor ?? "-",
            reference: transactionsDetail.paymentReference ?? "-",
            iban: transactionsDetail.creditorIBAN ?? "-",
          }),
        }
      : {
          title: t(Resources.Merchant.InitiateDetail.ShareTitle),
          text: t(Resources.Merchant.InitiateDetail.ShareText, {
            name: transactionsDetail.noteForDebtor ?? "Mo.one",
            amount,
          }),
        };

    try {
      await navigator.share({
        ...shareContent,
        url: `${appUrl("customer-payment-pay")}/${transactionsDetail.publicID}`,
      });
    } catch {} // error handling probably not necessary here
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%"
      px={[4, 0]}
      gap={[0, 20]}
    >
      <IconActionButton
        Icon={QrCodeScanner}
        label={t(Resources.Merchant.InitiateDetail.ActionButtons.Qr)}
        onClick={() => setDrawerState("qrCode")}
      />
      <IconActionButton
        Icon={Share}
        label={t(Resources.Merchant.InitiateDetail.ActionButtons.Share)}
        onClick={handleShareClick}
      />
      {!isInfoHidden && (
        <IconActionButton
          Icon={InfoOutlined}
          label={t(Resources.Merchant.InitiateDetail.ActionButtons.Info)}
          onClick={() => setDrawerState("info")}
        />
      )}
      <ContentDrawer
        drawerState={drawerState}
        onOpen={() => setDrawerState((prev) => prev)}
        onClose={() => setDrawerState("closed")}
        transactionsDetail={transactionsDetail}
        paypointName={paypoint?.name}
      />
    </Box>
  );
};
